/**
 * Copyright Highway9 Networks Inc.
 */

import { useEffect, useRef } from "react";
import { enqueueSnackbar, closeSnackbar, type SnackbarKey } from "notistack";
import { WarningListToastContent } from "~/components/Notifications/CustomToast";
import {
  useGoingOutOfSupportReleaseCallout,
  GOING_OUT_OF_SUPPORT_EDGE_BANNER_SUPPRESSED_KEY,
  GOING_OUT_OF_SUPPORT_EDGE_BANNER_SUPPRESSED_EDGES_KEY,
} from "./useGoingOutOfSupportReleaseCallout";

type Edge = { name?: string; goingOutOfSupport?: boolean; currentRelease?: string };

/**
 * Manages the going-out-of-support warning toast.
 *
 * Lifecycle:
 *   1. Edges flagged, not suppressed → toast shows, topbar chip hidden.
 *   2. User dismisses toast → suppress() writes to localStorage → isSuppressed=true →
 *      toast hides, topbar chip becomes visible (GoingOutOfSupportChip reads same key).
 *   3. Page refresh → localStorage still set → toast stays hidden, chip stays visible.
 *   4. New edge goes out of support → readSuppressedState detects new name, clears
 *      localStorage → isSuppressed=false → toast shows again, chip hides.
 */
export function useGoingOutOfSupportReleaseToast(edges: Edge[], onViewAll: () => void) {
  const { goingOutOfSupportEdges, isSuppressed, suppress } = useGoingOutOfSupportReleaseCallout(edges, {
    suppressedKey: GOING_OUT_OF_SUPPORT_EDGE_BANNER_SUPPRESSED_KEY,
    suppressedEdgesKey: GOING_OUT_OF_SUPPORT_EDGE_BANNER_SUPPRESSED_EDGES_KEY,
  });

  // Keep stable refs so the toast content closure always calls the latest version.
  const onViewAllRef = useRef(onViewAll);
  useEffect(() => { onViewAllRef.current = onViewAll; }, [onViewAll]);

  const suppressRef = useRef(suppress);
  useEffect(() => { suppressRef.current = suppress; }, [suppress]);

  const toastKeyRef = useRef<SnackbarKey | null>(null);

  const shouldShow = goingOutOfSupportEdges.length > 0 && !isSuppressed;
  const edgeNamesKey = goingOutOfSupportEdges.map((e) => e.name).join(",");

  useEffect(() => {
    if (toastKeyRef.current !== null) {
      closeSnackbar(toastKeyRef.current);
      toastKeyRef.current = null;
    }

    if (!shouldShow) return;

    const count = goingOutOfSupportEdges.length;
    const items = goingOutOfSupportEdges.map((e) => ({
      primary: e.name ?? "Unknown",
      secondary: e.currentRelease,
    }));

    const key = enqueueSnackbar("", {
      persist: true,
      anchorOrigin: { vertical: "top", horizontal: "right" },
      content: (id) => (
        <WarningListToastContent
          id={id}
          title={`${count} mobile ${count === 1 ? "edge" : "edges"} going out of support soon`}
          items={items}
          maxItems={2}
          actionLabel="View all"
          onAction={() => onViewAllRef.current()}
          onDismiss={() => suppressRef.current()}
        />
      ),
    });

    toastKeyRef.current = key;

    return () => {
      if (toastKeyRef.current !== null) {
        closeSnackbar(toastKeyRef.current);
        toastKeyRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow, edgeNamesKey]);
}
